import React, { useState } from 'react';
import { Form, Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import HeaderFirst from 'utils/HeaderFirst';
import { usePayDetteMutation } from 'actions/dettes/mutations';
import useDettesData from './useDettesData';
import { useGetComptePaiementQuery } from 'actions/compte_paiement/queries';

export default function PayDetteForm() {
  const { clientID: dossier_id } = useParams();
  const navigate = useNavigate();
  const [payDette] = usePayDetteMutation();
  const { updatedDettes } = useDettesData(dossier_id);
  const {data: compte_paiement } = useGetComptePaiementQuery({dossier_id})
  const [paiement, setPaiement] = useState({
    sortie_id: '',
    montant: '',
    source_paiement: '',
    notes: '',
  });

  const [selectedDette, setSelectedDette] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Options pour les sources de paiement
  /*const sourcePaiementOptions = {
    wave: 'Wave',
    orange_money: 'Orange Money',
    banque: 'Banque',
    caisse: 'Caisse',
  };*/

  // Filtrer les dettes avec solde restant
  const dettesDisponibles = updatedDettes.filter(
    (dette) => dette.reste_a_payer > 0 && !dette.archived
  );

  const handleDetteChange = (event) => {
    const detteId = event.target.value;
    const dette = updatedDettes.find((d) => d.id === parseInt(detteId));

    if (dette) {
      setSelectedDette(dette);
      setPaiement((prev) => ({
        ...prev,
        sortie_id: detteId,
        montant: dette.reste_a_payer,
      }));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setPaiement((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!paiement.sortie_id) {
      toast.error('Veuillez sélectionner une dette.');
      return;
    }

    if (!paiement.montant || parseFloat(paiement.montant) <= 0) {
      toast.error('Veuillez entrer un montant valide.');
      return;
    }

    if (!paiement.source_paiement) {
      toast.error('Veuillez sélectionner une source de paiement.');
      return;
    }

    if (parseFloat(paiement.montant) > selectedDette.reste_a_payer) {
      toast.error(
        `Le montant ne peut pas dépasser le solde restant (${selectedDette.reste_a_payer})`
      );
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        sortie_id: parseInt(paiement.sortie_id),
        montant: parseFloat(paiement.montant),
        source_paiement: paiement.source_paiement,
        ...(paiement.notes && { notes: paiement.notes }),
      };

      await payDette(payload).unwrap();
      toast.success('Paiement enregistré avec succès');
      navigate(-1);
    } catch (error) {
      if (error.status === 404) {
        toast.error('Dette non trouvée');
      } else if (error.status === 400) {
        toast.error(
          error.data?.message || 'Erreur lors de l\'enregistrement du paiement'
        );
      } else {
        toast.error(
          `Erreur: ${error.data?.message || 'Échec de l\'enregistrement du paiement'}`
        );
      }
      console.error('Erreur paiement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '15px',
      }}
    >
      <HeaderFirst nom={<h4 className="titre-dossier">Paiement au fournisseur</h4>} />
      <div className="w-100 w-md-75 px-0 px-md-5 container-background">
        <Container fluid className="container-style p-4 mb-5">
          <Form onSubmit={handleSubmit}>
            <div className="d-flex flex-column align-items-center gap-2">
              {/* Sélection de la dette */}
              <div className="w-100">
                <Row>
                  <Col sm={12}>
                    <Form.Group controlId="formDette">
                      <Form.Label>Dette à payer</Form.Label>
                      <Form.Control
                        as="select"
                        name="sortie_id"
                        value={paiement.sortie_id}
                        onChange={handleDetteChange}
                        className="rounded rounded-pill"
                      >
                        <option value="">- Sélectionnez une dette -</option>
                        {dettesDisponibles.length > 0 ? (
                          dettesDisponibles.map((dette) => (
                            <option key={dette.id} value={dette.id}>
                              {dette.intitule} - {dette.fournisseurs_details?.nom} (
                              Solde: {dette.reste_a_payer})
                            </option>
                          ))
                        ) : (
                          <option disabled>Aucune dette à payer</option>
                        )}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Montant à payer */}
              <div className="w-100">
                <Row>
                  <Col sm={6}>
                    <Form.Group controlId="formMontant">
                      <Form.Label>Montant à payer</Form.Label>
                      <Form.Control
                        type="number"
                        name="montant"
                        value={paiement.montant}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group controlId="formSourcePaiement">
                      <Form.Label>Source de paiement</Form.Label>
                      <Form.Control
                        as="select"
                        name="source_paiement"
                        value={paiement.source_paiement}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      >
                        <option value="">- Sélectionnez -</option>
                        {/*Object.entries(sourcePaiementOptions).map(
                          ([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          )
                        )*/}
                        {compte_paiement?.data?.map((compte) => (
                          <option key={compte.id} value={compte.source_paiement}>
                            {compte.compte}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Notes optionnelles */}
              <div className="w-100">
                <Row>
                  <Col sm={12}>
                    <Form.Group controlId="formNotes">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="notes"
                        value={paiement.notes}
                        onChange={handleInputChange}
                        className="rounded"
                        rows={3}
                        placeholder="Ajouter une description sur ce paiement..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Boutons */}
              <Row className="mt-3 w-100">
                <Col sm={12} style={{ textAlign: 'right' }}>
                  <div className="d-flex gap-3 justify-content-end">
                    <button
                      onClick={() => navigate(-1)}
                      className="btn btn-danger rounded rounded-pill px-4"
                      disabled={isLoading}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary rounded rounded-pill px-4"
                      style={{
                        backgroundColor: '#068f7a',
                        borderColor: '#068f7a',
                      }}
                      disabled={
                        !paiement.sortie_id ||
                        !paiement.montant ||
                        !paiement.source_paiement ||
                        isLoading
                      }
                    >
                      {isLoading ? 'Enregistrement...' : 'Enregistrer le paiement'}
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        </Container>
      </div>
    </div>
  );
}
